/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { useIsFocused } from '@react-navigation/native';
import React, { FC, useCallback, useEffect } from 'react';
import { Platform } from 'react-native';
import styled from 'styled-components/native';

import appsFlyerSDK from '@webtrader/core/appsflyer';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { getDialCodeFromCountryKey } from '@webtrader/core/format/country';
import { useTranslation } from '@webtrader/core/i18n';
import { ALL_MOBILE_ROUTES } from '@webtrader/core/router/mobile';
import { countryService } from '@webtrader/core/services/country';
import { demoCreationService, useDemoCreation } from '@webtrader/core/services/demoCreation';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { isValidEmail } from '@webtrader/core/validators';

import { AlertManager } from '@/components/Alert/Manager';
import Button from '@/components/Button';
import AntDesign from '@/components/Icons/AntDesign';
import CountryDropdown from '@/screens/auth/DemoCreation/Country';
import { getLoginSource } from '@/utils/login-source';
import { replace } from '@/utils/navigation';

import DepositDropdown from './Deposit';
import PhoneNumberTextField from './PhoneNumber';
import DemoCreationTermsAndConditions from './TermsAndConditions';
import DemoCreationFormTextField from './TextField';

interface ICreationFormProps {
  publicAuth?: boolean;
  accountName?: string;
  setNeedUpdateFields?: (value: boolean) => void;
}

const CreationForm: FC<ICreationFormProps> = (props) => {
  const { publicAuth, accountName, setNeedUpdateFields } = props;

  const { t } = useTranslation();
  const { theme } = useTheme();
  const focused = useIsFocused();

  const {
    values,
    submitting,
    showFirstNameValidation,
    showLastNameValidation,
    showEmailValidation,
    showCountryValidation,
    showMobileValidation,
    showDepositValidation
  } = useDemoCreation();

  const { firstName, lastName, email, country, countryKey, mobile, deposit } = values;

  const onMobileSumbit = useCallback(() => {
    const { firstName, lastName, email, country, countryKey, mobile, deposit } = values || {};
    const dialCode = getDialCodeFromCountryKey(countryKey);

    const firstNameNotCompleted = showFirstNameValidation || !firstName;
    const lastNameNotCompleted = showLastNameValidation || !lastName;
    const emailNotCompleted = showEmailValidation || !email;
    const countryCodeNotCompleted = showCountryValidation || !country;
    const mobileNotCompleted = showMobileValidation || !dialCode || !mobile;
    const depositNotCompleted = showDepositValidation || !deposit;

    if (
      firstNameNotCompleted ||
      lastNameNotCompleted ||
      emailNotCompleted ||
      countryCodeNotCompleted ||
      showMobileValidation ||
      mobileNotCompleted ||
      depositNotCompleted
    ) {
      return;
    }

    demoCreationService.onMobileSubmit(
      {
        onComplete: (success, message) => {
          if (success) {
            appsFlyerSDK.trackDemoSignup({
              phoneNumber: `${dialCode}${mobile}`,
              email,
              name: `${firstName} ${lastName}`
            });

            replace(publicAuth ? ALL_MOBILE_ROUTES.AUTH.DEMO_COMPLETION : ALL_MOBILE_ROUTES.MAIN.DEMO_COMPLETION, {
              publicAuth,
              setNeedUpdateFields
            });
          } else {
            AlertManager().alert({
              message
            });
          }
        },
        platform: getLoginSource(Platform.OS)
      },
      publicAuth
    );
  }, [
    values,
    showFirstNameValidation,
    showLastNameValidation,
    showEmailValidation,
    showCountryValidation,
    showMobileValidation,
    showDepositValidation,
    publicAuth,
    setNeedUpdateFields
  ]);

  const onSubmit = useCallback(() => {
    const { firstName, lastName, email, country, countryKey, mobile, deposit } = values;
    demoCreationService.onFirstNameChange(firstName);
    demoCreationService.onLastNameChange(lastName);
    demoCreationService.onEmailChange(email);
    demoCreationService.onCountryChange(country);
    demoCreationService.onDialCodeChange(countryKey);
    demoCreationService.onMobileChange(mobile);
    demoCreationService.onDepositChange(deposit);

    onMobileSumbit();
  }, [values, onMobileSumbit]);

  useEffect(() => {
    countryService.init();
    demoCreationService.fetchDemoDepositAmount();
  }, [publicAuth, accountName, focused]);

  return (
    <StyledForm>
      <DemoCreationFormTextField
        icon={<AntDesign name="user" size={24} color={theme.colors.text[1]} />}
        title={t('First Name')}
        value={firstName}
        validationText={t('First name is required')}
        showValidation={showFirstNameValidation}
        onChange={demoCreationService.onFirstNameChange}
        testID={EDataTestId.DEMO_ACCOUNT_CREATION_MODAL_FIRST_NAME}
      />
      <DemoCreationFormTextField
        icon={<AntDesign name="user" size={24} color={theme.colors.text[1]} />}
        title={t('Last Name')}
        value={lastName}
        validationText={t('Last name is required')}
        showValidation={showLastNameValidation}
        onChange={demoCreationService.onLastNameChange}
        testID={EDataTestId.DEMO_ACCOUNT_CREATION_MODAL_LAST_NAME}
      />
      <DemoCreationFormTextField
        icon={<AntDesign name="mail" size={24} color={theme.colors.text[1]} />}
        title={t('Email')}
        value={email}
        validationText={email && !isValidEmail(email) ? t('Email format invalid') : t('Email is required')}
        showValidation={showEmailValidation}
        keyboardType="email-address"
        onChange={demoCreationService.onEmailChange}
        testID={EDataTestId.DEMO_ACCOUNT_CREATION_MODAL_EMAIL}
      />
      <CountryDropdown
        value={country}
        onChange={demoCreationService.onCountryChange}
        showValidation={showCountryValidation}
        validationText={t('Country is required.')}
      />
      <PhoneNumberTextField
        countryKey={countryKey}
        value={mobile}
        showValidation={showMobileValidation}
        onChange={demoCreationService.onMobileChange}
        onDialCodeChange={demoCreationService.onDialCodeChange}
      />
      <DepositDropdown
        title={t('Deposit')}
        value={deposit}
        validationText={t('Deposit is required')}
        showValidation={showDepositValidation}
        onChange={demoCreationService.onDepositChange}
      />
      <DemoCreationTermsAndConditions />
      <StyledFormActions>
        <Button
          variant="subtle"
          loading={submitting}
          onPress={() => onSubmit()}
          testID={EDataTestId.DEMO_ACCOUNT_CREATION_MODAL_CONFIRM}
        >
          {t('AGREE AND CONTINUE')}
        </Button>
      </StyledFormActions>
      <StyledGap />
    </StyledForm>
  );
};

export default CreationForm;

const StyledForm = styled.View`
  background-color: ${({ theme }) => theme.colors.background[1]};
  width: 100%;
`;

const StyledFormActions = styled.View`
  background-color: ${({ theme }) => theme.colors.background[0]};
`;

const StyledGap = styled.View`
  height: ${dynamicSize.convertSize(20)}px;
`;
